/**
 * Conversation Service — SmartCHG threads adapter.
 *
 * Maps SmartCHG's `/api/v1/threads/*` (BE-2) response shape onto the
 * ConversationSidebar / useConversationSidebar contract used by Smart
 * Insights history. Only the service layer translates backend DTOs.
 *
 * Translation contract:
 *   Backend thread {thread_id, title, created_at, updated_at, message_count}
 *     → Conversation { conversationId, title, startedAt, updatedAt,
 *                      messageCount, dataSourceName? }
 *   Backend message {message_id, role, content (raw text or JSON string),
 *                    created_at, reasoning_steps?, references?}
 *     → ConversationMessage { messageId, role, content (string), message
 *                             (raw, only for assistant), timestamp, senderType,
 *                             botMessageId?, humanMessageId? }
 *
 * Assistant `content` is a JSON-stringified payload per CLAUDE.md C1:
 *   {status, query, normal_text, summary, insights, metrics, charts,
 *    intelligence_analysis, source_data, error_message?}
 * The sidebar's transformer expects to find this raw JSON on the `message`
 * field of the rehydrated ConversationMessage so the dashboard re-render path
 * can consume it; we mirror `content` → `message` for assistant rows.
 */

import { conversationAPI } from './api';
import { logInfo, logError } from '../utils/logger';
import { displayTextFromStructuredPayload } from '../utils/messageParser';

// --- UI-facing shapes (frozen — must match consumer expectations) ------------

export interface Conversation {
  conversationId: string;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
  startedAt?: string;
  endedAt?: string;
  messageCount?: number;
  lastMessage?: string;
  userId?: string;
  username?: string;
  agentId?: string;
  /** Smart Insights data source name for "Query asked on [data source]" (from backend). */
  dataSourceName?: string;
  isFavourite?: boolean;
  [key: string]: unknown;
}

export interface StoredFeedback {
  rating: 'thumbs_up' | 'thumbs_down';
  text?: string | null;
  submitted_at?: string;
  submitted_by?: string;
}

export interface ConversationMessage {
  messageId?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  senderType?: 'HUMAN' | 'BOT';
  /** For bot messages: links to the corresponding user message. */
  humanMessageId?: string;
  /** Bot message ID for feedback. */
  botMessageId?: string;
  /** Persisted feedback on this message (None when never given). */
  feedback?: StoredFeedback | null;
  /**
   * Rehydrated dashboard payload (parsed from assistant content JSON).
   * Loose-typed because the Smart Insights ChatInterface owns the canonical
   * `DashboardData` shape and re-narrows locally.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dashboardData?: any;
  /** Mirrors `content` for ChatInterface's display path (legacy alias). */
  message?: string;
  isLiveQuery?: boolean;
  normal_text?: string;
  suggestedQueries?: string[];
  status?: string;
  [key: string]: unknown;
}

export interface ConversationsResponse {
  conversations: Conversation[];
  success?: boolean;
  error?: string;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  nextOffset?: number | null;
  [key: string]: unknown;
}

export interface MessagesResponse {
  messages: ConversationMessage[];
  conversationId?: string;
  success?: boolean;
  error?: string;
  [key: string]: unknown;
}

export interface ConversationBookmarkResponse {
  success: boolean;
  bookmarks: ConversationBookmark[];
  error?: string;
  [key: string]: unknown;
}

export interface ConversationBookmark {
  id: string;
  conversationId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

// --- Backend wire shapes (BE-2) ----------------------------------------------

interface BackendThread {
  thread_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  message_count?: number;
  is_favourite?: boolean;
  /** Some payloads include a denormalized last-message preview / datasource name. */
  last_message?: string | null;
  data_source_name?: string | null;
}

interface BackendMessage {
  message_id: string;
  role: 'user' | 'assistant' | 'system';
  /** Plain display text. Newer backend responses expose assistant payload separately as `structured`. */
  content: string;
  structured?: AssistantPayload | null;
  created_at: string;
  reasoning_steps?: unknown;
  references?: unknown;
  feedback?: StoredFeedback | null;
}

interface ListThreadsBody {
  threads: BackendThread[];
  limit?: number;
  offset?: number;
  has_more?: boolean;
  next_offset?: number | null;
}

interface ThreadMessagesBody {
  thread: BackendThread;
  messages: BackendMessage[];
}

const LOCAL_BOOKMARK_STORAGE_KEY = 'smartchg:finance-bot-v2:conversation-bookmarks';

function readLocalBookmarks(): ConversationBookmark[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_BOOKMARK_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalBookmarks(bookmarks: ConversationBookmark[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_BOOKMARK_STORAGE_KEY, JSON.stringify(bookmarks));
}

// --- Helpers -----------------------------------------------------------------

/**
 * Parse an assistant message's JSON payload (CLAUDE.md C1).
 * Returns the parsed object, or `null` if `raw` isn't valid JSON
 * (caller falls back to displaying raw text).
 */
export interface AssistantPayload {
  status?: string;
  query?: string;
  normal_text?: string;
  summary?: string;
  insights?: unknown[];
  metrics?: unknown;
  charts?: unknown[];
  intelligence_analysis?: unknown;
  source_data?: unknown;
  error_message?: string;
  [key: string]: unknown;
}

function displayTextFromAssistantPayload(payload: AssistantPayload): string | undefined {
  return displayTextFromStructuredPayload(payload);
}

export function parseAssistantContent(raw: string): AssistantPayload | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    const parsed = JSON.parse(trimmed) as AssistantPayload;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Build a short preview string from an assistant message (used for the sidebar
 * row's "lastMessage" line). Falls back gracefully when the JSON shape is unknown.
 */
function previewFromAssistantContent(raw: string): string {
  const parsed = parseAssistantContent(raw);
  if (!parsed) {
    return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
  }
  const displayText = displayTextFromAssistantPayload(parsed);
  if (displayText) {
    return displayText.trim().slice(0, 120);
  }
  const parts: string[] = [];
  if (Array.isArray(parsed.charts) && parsed.charts.length > 0) {
    parts.push(`${parsed.charts.length} chart${parsed.charts.length > 1 ? 's' : ''}`);
  }
  if (Array.isArray(parsed.insights) && parsed.insights.length > 0) {
    parts.push(`${parsed.insights.length} insight${parsed.insights.length > 1 ? 's' : ''}`);
  }
  if (parts.length > 0) return `Response with ${parts.join(', ')}`;
  return 'Structured response';
}

function mapThread(t: BackendThread): Conversation {
  return {
    conversationId: t.thread_id,
    title: t.title ?? undefined,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    // Sidebar primarily reads `startedAt` for time formatting; treat
    // updated_at as the "started" baseline so the row sorts/displays sensibly.
    startedAt: t.updated_at ?? t.created_at,
    endedAt: t.updated_at,
    messageCount: t.message_count ?? 0,
    lastMessage: t.last_message ?? undefined,
    dataSourceName: t.data_source_name ?? undefined,
    isFavourite: Boolean(t.is_favourite),
  };
}

function mapMessage(m: BackendMessage): ConversationMessage {
  const isAssistant = m.role === 'assistant';
  const isUser = m.role === 'user';

  // For assistant rows the JSON payload is the *content* string; restored
  // Smart Insights threads re-read this off the `message` field.
  const result: ConversationMessage = {
    messageId: m.message_id,
    role: m.role,
    content: m.content ?? '',
    timestamp: m.created_at,
    senderType: isAssistant ? 'BOT' : isUser ? 'HUMAN' : undefined,
  };

  if (isAssistant) {
    const structured = m.structured && typeof m.structured === 'object'
      ? m.structured
      : parseAssistantContent(m.content ?? '');
    const rawPayload = structured ? JSON.stringify(structured) : (m.content ?? '');

    // Mirror raw structured JSON onto `message` so restored Smart Insights
    // history keeps the full payload (charts, source_data, etc.).
    (result as { message?: string }).message = rawPayload;
    if (structured) {
      result.dashboardData = structured;
      result.normal_text = displayTextFromAssistantPayload(structured);
    }
    result.botMessageId = m.message_id;
    if (m.feedback) {
      result.feedback = m.feedback;
    }

    // Extract suggestedQueries (read by the sidebar) when present.
    const suggested = structured && Array.isArray((structured as { suggested_queries?: unknown[] }).suggested_queries)
      ? ((structured as { suggested_queries?: unknown[] }).suggested_queries as unknown[])
      : null;
    if (suggested && suggested.length > 0) {
      (result as { suggestedQueries?: unknown[] }).suggestedQueries = suggested;
    }
  }

  return result;
}

// --- Service -----------------------------------------------------------------

export class ConversationService {
  static async getUserConversations(
    searchQuery?: string,
    options?: { limit?: number; offset?: number }
  ): Promise<ConversationsResponse> {
    try {
      const search = searchQuery?.trim() || undefined;
      const params = {
        ...(search ? { search } : {}),
        ...(typeof options?.limit === 'number' ? { limit: options.limit } : {}),
        ...(typeof options?.offset === 'number' ? { offset: options.offset } : {}),
      };
      logInfo('ConversationService', 'Fetching user threads');
      const response = await conversationAPI.listThreads(Object.keys(params).length > 0 ? params : undefined);
      if (response.status === 200 && response.data) {
        const body = response.data as Partial<ListThreadsBody>;
        const threads: BackendThread[] = Array.isArray(body.threads) ? body.threads : [];
        const conversations = threads.map(mapThread);
        logInfo('ConversationService', `Fetched ${conversations.length} threads`);
        return {
          conversations,
          success: true,
          limit: body.limit,
          offset: body.offset,
          hasMore: body.has_more,
          nextOffset: body.next_offset ?? null,
        };
      }
      return { conversations: [], success: false };
    } catch (error: unknown) {
      logError(
        'ConversationService',
        'Failed to fetch threads',
        error instanceof Error ? error : new Error(String(error)),
      );
      const errMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (error instanceof Error ? error.message : 'Failed to fetch conversations');
      return { conversations: [], success: false, error: errMessage };
    }
  }

  /**
   * Load all messages for a thread. Backend returns ASC ordered messages
   * (CLAUDE.md C2). We forward that order to the sidebar transformer.
   */
  static async getConversationMessages(
    conversationId: string,
    options: { signal?: AbortSignal; timeoutMs?: number } = {},
  ): Promise<MessagesResponse> {
    try {
      if (!conversationId || conversationId.trim() === '') {
        throw new Error('Conversation ID is required');
      }
      logInfo('ConversationService', `Fetching messages for thread: ${conversationId}`);
      const response = await conversationAPI.getThreadMessages(conversationId, {
        signal: options.signal,
        timeout: options.timeoutMs,
      });

      if (response.status === 200 && response.data) {
        const body = response.data as Partial<ThreadMessagesBody>;
        const backendMessages: BackendMessage[] = Array.isArray(body.messages) ? body.messages : [];
        const messages = backendMessages.map(mapMessage);

        // Surface dataSourceName/title back through the response so the
        // sidebar can pass it to onSelectConversation.
        const extra: Record<string, unknown> = {};
        if (body.thread) {
          extra.thread = body.thread;
          extra.title = body.thread.title ?? undefined;
        }

        logInfo(
          'ConversationService',
          `Fetched ${messages.length} messages for thread ${conversationId}`,
        );
        return { messages, conversationId, success: true, ...extra };
      }
      return { messages: [], conversationId, success: false };
    } catch (error: unknown) {
      if (options.signal?.aborted) {
        return { messages: [], conversationId, success: false };
      }
      const errResp = (error as { response?: { data?: { error?: string; message?: string }; status?: number } })?.response;
      const errMessage =
        errResp?.data?.error ?? errResp?.data?.message ??
        (error instanceof Error ? error.message : 'Failed to fetch messages');
      logError(
        'ConversationService',
        `Failed to fetch messages for thread ${conversationId} (status=${errResp?.status ?? 'n/a'})`,
        error instanceof Error ? error : new Error(String(error)),
      );
      return { messages: [], conversationId, success: false, error: errMessage };
    }
  }

  /** Rename a thread (PATCH /api/v1/threads/{id}). */
  static async renameConversation(
    conversationId: string,
    title: string,
  ): Promise<{ success: boolean; title?: string; updatedAt?: string; error?: string }> {
    try {
      if (!conversationId) return { success: false, error: 'Conversation ID is required' };
      const response = await conversationAPI.renameThread(conversationId, title);
      if (response.status === 200 && response.data) {
        const data = response.data as { title?: string; updated_at?: string; updatedAt?: string };
        return { success: true, title: data.title, updatedAt: data.updated_at ?? data.updatedAt };
      }
      return { success: false, error: 'Failed to rename conversation' };
    } catch (error: unknown) {
      const errMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (error instanceof Error ? error.message : 'Failed to rename conversation');
      logError(
        'ConversationService',
        'Failed to rename thread',
        error instanceof Error ? error : new Error(String(error)),
      );
      return { success: false, error: errMessage };
    }
  }

  static async setConversationFavourite(
    conversationId: string,
    isFavourite: boolean,
  ): Promise<{ success: boolean; isFavourite?: boolean; updatedAt?: string; error?: string }> {
    try {
      if (!conversationId) return { success: false, error: 'Conversation ID is required' };
      const response = await conversationAPI.setThreadFavourite(conversationId, isFavourite);
      if (response.status === 200 && response.data) {
        const data = response.data as { is_favourite?: boolean; updated_at?: string; updatedAt?: string };
        return {
          success: true,
          isFavourite: Boolean(data.is_favourite),
          updatedAt: data.updated_at ?? data.updatedAt,
        };
      }
      return { success: false, error: 'Failed to update favourite' };
    } catch (error: unknown) {
      const errMessage =
        (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ??
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error instanceof Error ? error.message : 'Failed to update favourite');
      logError(
        'ConversationService',
        'Failed to update thread favourite',
        error instanceof Error ? error : new Error(String(error)),
      );
      return { success: false, error: errMessage };
    }
  }

  /**
   * Delete a thread (DELETE /api/v1/threads/{id}). Backend returns 204 on
   * success per CLAUDE.md C2; the sidebar treats `success: true` as the
   * sole signal.
   */
  static async deleteConversation(
    conversationId: string,
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      if (!conversationId || conversationId.trim() === '') {
        return { success: false, error: 'Conversation ID is required' };
      }
      logInfo('ConversationService', `Deleting thread: ${conversationId}`);
      const response = await conversationAPI.deleteThread(conversationId);
      // 204 = success (no body); 200 also tolerated for forward-compat.
      const success = response.status === 204 || response.status === 200;
      if (success) {
        logInfo('ConversationService', 'Thread deleted', { conversationId });
        return { success: true, message: 'Conversation deleted' };
      }
      return { success: false, error: 'Failed to delete conversation' };
    } catch (error: unknown) {
      const errResp = (error as { response?: { data?: { error?: string; message?: string } } })?.response;
      const errMessage =
        errResp?.data?.error ?? errResp?.data?.message ??
        (error instanceof Error ? error.message : 'Failed to delete conversation');
      logError(
        'ConversationService',
        'Failed to delete thread',
        error instanceof Error ? error : new Error(String(error)),
      );
      return { success: false, error: errMessage };
    }
  }

  // --- Bookmarks ------------------------------------------------------------
  //
  // SmartCHG v1 originally had no bookmark endpoint. The legacy fallback keeps
  // saved threads locally without calling unwhitelisted paths.

  static async getConversationBookmarks(): Promise<ConversationBookmarkResponse> {
    const bookmarks = readLocalBookmarks().sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    return { success: true, bookmarks };
  }

  static async saveConversationBookmark(
    conversationId: string,
    title?: string,
  ): Promise<ConversationBookmark | null> {
    if (!conversationId) return null;

    const now = new Date().toISOString();
    const bookmarks = readLocalBookmarks();
    const existing = bookmarks.find((bookmark) => bookmark.conversationId === conversationId);
    const next: ConversationBookmark = existing
      ? {
          ...existing,
          title: title || existing.title,
          updatedAt: now,
        }
      : {
          id: `local-${conversationId}`,
          conversationId,
          title: title || 'Untitled thread',
          createdAt: now,
          updatedAt: now,
        };

    writeLocalBookmarks([
      next,
      ...bookmarks.filter((bookmark) => bookmark.conversationId !== conversationId),
    ]);
    return next;
  }

  static async renameConversationBookmark(
    id: string,
    title: string,
  ): Promise<ConversationBookmark | null> {
    const bookmarks = readLocalBookmarks();
    const existing = bookmarks.find((bookmark) => bookmark.id === id);
    if (!existing) return null;

    const updated = { ...existing, title, updatedAt: new Date().toISOString() };
    writeLocalBookmarks(bookmarks.map((bookmark) => bookmark.id === id ? updated : bookmark));
    return updated;
  }

  static async deleteConversationBookmark(id: string): Promise<boolean> {
    const bookmarks = readLocalBookmarks();
    const next = bookmarks.filter((bookmark) => bookmark.id !== id);
    if (next.length === bookmarks.length) return false;
    writeLocalBookmarks(next);
    return true;
  }
}

// Expose the helper so other components can lazily parse stored assistant
// content when rendering Smart Insights history.
export { previewFromAssistantContent };
